import { useState, useRef } from 'react';
import { usePlans } from '@/hooks/usePlans';
import { PLAN_TYPES } from '@/config/api';
import { HomeFeaturedCard } from '@/components/molecules';
import { CtaCard } from '@/components/atoms';
import lemonHappy from '@/assets/img/lemon-happy.svg';
import lemonShy from '@/assets/img/lemon-shy.svg';
import arrowRight from '@/assets/icons/arrow-right.svg';
import './ComparatorHighlight.scss';

const FEATURED_COUNT = 5;

// Touch drag tuning.
const SWIPE_COMMIT_RATIO = 0.25; // fraction of card width that validates a slide
const SWIPE_COMMIT_MIN_PX = 70; // absolute floor for the commit threshold
const DIRECTION_LOCK_PX = 10; // movement before locking horizontal vs vertical
const CLICK_CANCEL_PX = 8; // movement beyond this cancels the underlying link click
const MAX_DRAG_ROTATION = 6; // deg, slight tilt for the "card" feel

export function ComparatorHighlight() {
  const { plans } = usePlans(PLAN_TYPES.MOBILE);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<'next' | 'prev' | null>(null);
  const [exitIndex, setExitIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Drag bookkeeping lives in refs so following the finger never re-renders.
  const activeCardRef = useRef<HTMLDivElement | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const dragXRef = useRef(0);
  const isHorizontalRef = useRef<boolean | null>(null); // null = undecided, true = H lock, false = V
  const didDragRef = useRef(false); // gates the anti-click guard
  const activePointerIdRef = useRef<number | null>(null);

  const featuredPlans = plans.slice(0, FEATURED_COUNT);
  const currentPlan = featuredPlans[currentIndex] ?? null;

  const handlePrev = () => {
    if (featuredPlans.length === 0) return;
    setDirection('prev');
    setExitIndex(currentIndex);
    setCurrentIndex((prev) => (prev - 1 + featuredPlans.length) % featuredPlans.length);
  };

  const handleNext = () => {
    if (featuredPlans.length === 0) return;
    setDirection('next');
    setExitIndex(currentIndex);
    setCurrentIndex((prev) => (prev + 1) % featuredPlans.length);
  };

  // Reset so the carousel returns to idle and a new drag can start, once the
  // CSS exit animation (active-exit, 0.4s, ComparatorHighlight.scss) finishes.
  const handleExitAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    setExitIndex(null);
    setDirection(null);
  };

  // Follow the finger in real time, mutating the DOM directly (no re-render).
  // Transition is killed inline so the card tracks the finger without lag.
  const applyDragTransform = (dx: number) => {
    const el = activeCardRef.current;
    if (!el) return;
    const rot = Math.max(-MAX_DRAG_ROTATION, Math.min(MAX_DRAG_ROTATION, dx / 20));
    el.style.transition = 'none';
    el.style.transform = `translateX(${dx}px) rotate(${rot}deg)`;
    el.style.opacity = String(Math.max(0.6, 1 - Math.abs(dx) / 600));
  };

  // Clear inline styles in one recalc: restoring the transition before wiping
  // the transform lets the default CSS transition animate the snap-back, and
  // lets the keyframe animation own the transform on commit.
  const clearDragStyles = () => {
    const el = activeCardRef.current;
    if (!el) return;
    el.style.transition = '';
    el.style.transform = '';
    el.style.opacity = '';
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Touch only; skip when nothing to swipe or an animation is running.
    if (e.pointerType === 'mouse') return;
    if (featuredPlans.length <= 1 || direction !== null) return;
    if (!e.isPrimary) return;

    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    dragXRef.current = 0;
    isHorizontalRef.current = null;
    didDragRef.current = false;
    activePointerIdRef.current = e.pointerId;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current;
    if (!start || activePointerIdRef.current !== e.pointerId) return;

    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;

    // Lock direction once the movement is significant.
    if (isHorizontalRef.current === null) {
      if (Math.abs(dx) < DIRECTION_LOCK_PX && Math.abs(dy) < DIRECTION_LOCK_PX) return;
      isHorizontalRef.current = Math.abs(dx) > Math.abs(dy);
      if (isHorizontalRef.current) {
        // Own the pointer stream and disable the CSS transition while dragging.
        e.currentTarget.setPointerCapture(e.pointerId);
        setIsDragging(true);
      }
    }

    // Vertical gesture: let the browser scroll the page.
    if (isHorizontalRef.current !== true) return;

    if (Math.abs(dx) > CLICK_CANCEL_PX) didDragRef.current = true;
    dragXRef.current = dx;
    applyDragTransform(dx);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current !== e.pointerId) return;

    const wasHorizontal = isHorizontalRef.current === true;
    const dx = dragXRef.current;

    pointerStartRef.current = null;
    isHorizontalRef.current = null;
    activePointerIdRef.current = null;

    if (!wasHorizontal) {
      setIsDragging(false);
      return;
    }

    const width = e.currentTarget.getBoundingClientRect().width || 1;
    const threshold = Math.min(width * SWIPE_COMMIT_RATIO, SWIPE_COMMIT_MIN_PX);

    setIsDragging(false);

    if (dx <= -threshold) {
      clearDragStyles();
      handleNext(); // swipe left -> next card
    } else if (dx >= threshold) {
      clearDragStyles();
      handlePrev(); // swipe right -> previous card
    } else {
      // Below threshold: clear inline styles, the CSS transition snaps back.
      clearDragStyles();
    }
  };

  // Swallow the synthetic click that follows a real drag so the underlying
  // <a> (window.open via onClick) never fires.
  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (didDragRef.current) {
      e.preventDefault();
      e.stopPropagation();
      didDragRef.current = false;
    }
  };

  return (
    <section className="comparator-highlight">
      <div className="comparator-highlight__content">
        <div className="comparator-highlight__left">
          <h1 className="comparator-highlight__title">
            Comparateur Forfait Mobile et Box Internet&nbsp;: Économisez en 30 secondes
          </h1>
          <p className="comparator-highlight__subtitle">
            Comparez plus de 30 opérateurs (Orange, Bouygues, Free...) et réduisez vos factures Telecom &amp; VPN.
          </p>
          <div className="comparator-highlight__ctas">
            <CtaCard
              href="/forfaits-mobiles-comparateur/"
              mascot={lemonHappy}
              title="Forfaits Mobile"
              label="Comparer"
            />
            <CtaCard
              href="/comparateur/"
              mascot={lemonShy}
              title="Box Internet"
              label="Comparer"
            />
          </div>
        </div>
        <div className="comparator-highlight__right">
          <span className="comparator-highlight__tagline">Comparez tout, payez moins</span>
          <div className={`comparator-highlight__carousel${direction ? ` comparator-highlight__carousel--${direction}` : ''}`}>
            {featuredPlans.length > 1 && (
              <div
                key={featuredPlans[(currentIndex + 1) % featuredPlans.length].id}
                className="comparator-highlight__stacked-card comparator-highlight__stacked-card--middle"
              >
                <HomeFeaturedCard
                  planType={PLAN_TYPES.MOBILE}
                  planId={featuredPlans[(currentIndex + 1) % featuredPlans.length].id}
                  showAnnonce={false}
                />
              </div>
            )}
            {exitIndex !== null && featuredPlans[exitIndex] && (
              <div
                key={`exit-${featuredPlans[exitIndex].id}`}
                className="comparator-highlight__exit-card"
                onAnimationEnd={handleExitAnimationEnd}
              >
                <HomeFeaturedCard
                  planType={PLAN_TYPES.MOBILE}
                  planId={featuredPlans[exitIndex].id}
                  showAnnonce={false}
                />
              </div>
            )}
            {currentPlan && (
              <div
                ref={activeCardRef}
                key={currentPlan.id}
                className={`comparator-highlight__active-card${isDragging ? ' comparator-highlight__active-card--dragging' : ''}`}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onClickCapture={handleClickCapture}
              >
                <HomeFeaturedCard
                  planType={PLAN_TYPES.MOBILE}
                  planId={currentPlan.id}
                  showAnnonce={false}
                />
              </div>
            )}
          </div>
          {featuredPlans.length > 1 && (
              <div className="comparator-highlight__carousel-nav">
                <button
                    type="button"
                    className="comparator-highlight__carousel-btn comparator-highlight__carousel-btn--prev"
                    onClick={handlePrev}
                    aria-label="Plan précédent"
                >
                  <img src={arrowRight} alt="" aria-hidden="true" width={16} height={16} />
                </button>
                <button
                    type="button"
                    className="comparator-highlight__carousel-btn comparator-highlight__carousel-btn--next"
                    onClick={handleNext}
                    aria-label="Plan suivant"
                >
                  <img src={arrowRight} alt="" aria-hidden="true" width={16} height={16} />
                </button>
              </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default ComparatorHighlight;
